import { useState } from "react";
import { message, Upload } from "antd";
import { getUploadUrlApi } from "../../../../../../services/common.services";
import axios from "axios";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

export const useVideoUpload = (opId) => {
  const [uploadFileLoading, setUploadFileLoading] = useState(false);
  const [uploadValues, setUploadValues] = useState({
    videoFileList: [],
    videoPreviewURL: "",
    s3Path: null,
    timeSlot: "12:00",
    isLink: false,
    compressedFile: null,
  });

  const uploadProps = {
    beforeUpload: (file, fileList) => {
      const MAX_UPLOAD_SIZE = 30 * 1024 * 1024;
      let file_ext = file.name.split(".").slice(-1)[0].toLowerCase();
      const validVideoTypes = ["mp4", "mov", "avi", "m4v", "3gp"];

      if (!validVideoTypes.includes(file_ext)) {
        message.error(`${file.name} is not a valid video file type`);
        return Upload.LIST_IGNORE;
      }

      const totalSize = fileList.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_UPLOAD_SIZE) {
        message.error("The uploading file reaches the maximum limit");
        return Upload.LIST_IGNORE;
      }

      handleVideoUpload(file);
      return false;
    },
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => onSuccess("ok"), 0);
  };

  const handleVideoUpload = async (file) => {
    const MAX_UPLOAD_SIZE = 29 * 1024 * 1024;
    const loadingKey = "video_upload";

    message.loading({
      content: "Processing video...",
      key: loadingKey,
      duration: 0,
    });

    try {
      let uploadFile = file;

      if (file.size > MAX_UPLOAD_SIZE) {
        message.loading({
          content: "Compressing video...",
          key: loadingKey,
          duration: 0,
        });

        const ffmpeg = new FFmpeg();
        await ffmpeg.load();
        await ffmpeg.writeFile("input.mp4", await fetchFile(file));

        await ffmpeg.exec([
          "-i",
          "input.mp4",
          "-vcodec",
          "libx264",
          "-b:v",
          "800k",
          "-vf",
          "scale=1280:-1",
          "-preset",
          "fast",
          "output.mp4",
        ]);

        const data = await ffmpeg.readFile("output.mp4");
        const compressedBlob = new Blob([data.buffer], { type: file.type });
        uploadFile = new File([compressedBlob], `compressed-${file.name}`, {
          type: file.type,
        });
      }

      const res = await getUploadUrlApi(
        `path_key=${uploadFile.name}&admission_type=OP&admission_id=${opId}`
      );

      if (!res?.data) throw new Error("Failed to get upload URL");

      const formData = new FormData();
      Object.entries(res.data.fields).forEach(([k, v]) =>
        formData.append(k, v)
      );
      formData.append("file", uploadFile);

      await axios.post(res.data.url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedFile = {
        path: res.data.fields.key,
        type: uploadFile.type,
        url: `${res.data.url}/${res.data.fields.key}`,
      };

      setUploadValues((prev) => ({
        ...prev,
        videoFileList: [uploadedFile],
        s3Path: uploadedFile,
        videoPreviewURL: URL.createObjectURL(uploadFile),
        isLink: false,
        compressedFile: uploadFile,
      }));

      message.success({
        content: "Video uploaded successfully!",
        key: loadingKey,
        duration: 2,
      });
    } catch (error) {
      console.error("Upload failed:", error);
      message.error({
        content: "Video upload failed",
        key: loadingKey,
        duration: 3,
      });
    } finally {
      window.setTimeout(() => message.destroy(loadingKey), 3000);
    }
  };

  const resetUploadValues = () => {
    message.destroy("video_upload");
    setUploadValues({
      videoFileList: [],
      videoPreviewURL: "",
      s3Path: null,
      timeSlot: "12:00",
      isLink: false,
      compressedFile: null,
    });
  };

  return {
    uploadFileLoading,
    uploadValues,
    setUploadValues,
    uploadProps,
    dummyRequest,
    handleVideoUpload,
    resetUploadValues,
  };
};
